import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { cx } from '@/core/utils';

/** Movement before a press on the header becomes a drag rather than a tap, px. */
const DRAG_THRESHOLD = 6;
/** A flick faster than this (px/ms) snaps in its direction regardless of position. */
const FLICK_VELOCITY = 0.4;

interface Props {
  /** Always visible, even when collapsed; dragging it moves the sheet. */
  header: ReactNode;
  /** Revealed when expanded; scrolls when taller than the sheet. */
  children: ReactNode;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Accessible name of the sheet. */
  label: string;
  /** Accessible names of the handle in each state, e.g. "Show settings" / "Hide settings". */
  expandLabel: string;
  collapseLabel: string;
  /** Height of the part covering the map when collapsed (header + safe area), px. */
  onPeekHeightChange?: (px: number) => void;
}

/**
 * A two-position sheet docked to the bottom of the screen: collapsed it shows only its header,
 * expanded its content too. Drag or flick the header, or tap the handle, to move it.
 */
export function BottomSheet({
  header,
  children,
  expanded,
  onExpandedChange,
  label,
  expandLabel,
  collapseLabel,
  onPeekHeightChange,
}: Props) {
  const bodyId = useId();
  const sheetRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  /** Height of the part still visible when collapsed (header + safe area), px. */
  const [peek, setPeek] = useState(0);
  /** Offset while the user drags, px from the expanded position. */
  const [dragOffset, setDragOffset] = useState<number | null>(null);
  const drag = useRef<{
    startY: number;
    base: number;
    /** How far the sheet moves down when collapsed, px. */
    travel: number;
    active: boolean;
    lastY: number;
    lastT: number;
    v: number;
  }>(null);
  const suppressClick = useRef(false);

  useLayoutEffect(() => {
    const sheet = sheetRef.current!;
    const head = headerRef.current!;
    const measure = () => {
      const safeArea = parseFloat(getComputedStyle(sheet).paddingBottom) || 0;
      const px = head.offsetHeight + safeArea;
      setPeek(px);
      onPeekHeightChange?.(px);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(head);
    return () => observer.disconnect();
  }, [onPeekHeightChange]);

  // Escape collapses the sheet when focus is inside it.
  useEffect(() => {
    if (!expanded) return;
    const sheet = sheetRef.current!;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onExpandedChange(false);
    sheet.addEventListener('keydown', onKey);
    return () => sheet.removeEventListener('keydown', onKey);
  }, [expanded, onExpandedChange]);

  // At rest the collapsed offset is relative to the sheet's own height (100%), so content changing
  // height while collapsed keeps the header in place instead of jumping and animating back.
  const restTransform = expanded ? 'translateY(0)' : `translateY(calc(100% - ${peek}px))`;
  const transform = dragOffset === null ? restTransform : `translateY(${dragOffset}px)`;

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    suppressClick.current = false; // touch drags end without a click, so the flag may still be set
    const travel = Math.max(0, sheetRef.current!.offsetHeight - peek);
    drag.current = {
      startY: e.clientY,
      base: expanded ? 0 : travel,
      travel,
      active: false,
      lastY: e.clientY,
      lastT: e.timeStamp,
      v: 0,
    };
  }

  function onPointerMove(e: PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    if (!d.active) {
      if (Math.abs(dy) < DRAG_THRESHOLD) return;
      d.active = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.v = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setDragOffset(Math.min(d.travel, Math.max(0, d.base + dy)));
  }

  function onPointerUp(e: PointerEvent) {
    const d = drag.current;
    drag.current = null;
    if (!d?.active) return;
    suppressClick.current = true; // the release of a drag is not a tap on whatever is under it
    const pos = Math.min(d.travel, Math.max(0, d.base + e.clientY - d.startY));
    const collapse = Math.abs(d.v) > FLICK_VELOCITY ? d.v > 0 : pos > d.travel / 2;
    setDragOffset(null);
    onExpandedChange(!collapse);
  }

  function onPointerCancel() {
    drag.current = null;
    setDragOffset(null);
  }

  return (
    <section
      ref={sheetRef}
      aria-label={label}
      className={cx(
        'fixed inset-x-0 bottom-0 z-2 flex max-h-[min(85dvh,calc(100dvh-5rem))] flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_16px_rgba(0,0,0,.18)]',
        dragOffset === null &&
          'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out',
      )}
      style={{ transform }}
    >
      <div
        ref={headerRef}
        className="shrink-0 touch-none px-4 pb-3"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={(e) => {
          if (!suppressClick.current) return;
          suppressClick.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          className="-mx-4 flex h-7 w-[calc(100%+2rem)] cursor-grab items-center justify-center"
          aria-expanded={expanded}
          aria-controls={bodyId}
          aria-label={expanded ? collapseLabel : expandLabel}
          onClick={() => onExpandedChange(!expanded)}
        >
          <span className="h-1.5 w-10 rounded-full bg-neutral-300" />
        </button>
        {header}
      </div>
      <div
        id={bodyId}
        inert={!expanded}
        className={cx(
          'min-h-0 overflow-y-auto overscroll-contain px-4 motion-safe:transition-opacity',
          !expanded && dragOffset === null && 'opacity-0',
        )}
      >
        {children}
      </div>
    </section>
  );
}
